import React from "react";
import { Eye, Printer } from "lucide-react";
import { InvoiceData } from "../../../../../types/invoices";
import { EDSButton } from "../../../../eds/EDSButton";
import {
  calculateInvoiceGross,
  calculateInvoiceNet,
  calculateInvoiceTax,
  formatDecimal
} from "./invoiceUtils";

interface PreviousRevisionsProps {
  revisions: InvoiceData[];
  currencySymbol: string;
  onViewRevision: (revision: InvoiceData) => void;
  onPrintRevision: (revision: InvoiceData) => void;
}

const PreviousRevisions = ({
  revisions,
  currencySymbol,
  onViewRevision,
  onPrintRevision
}: PreviousRevisionsProps): React.JSX.Element | null => {
  if (revisions.length === 0) {
    return null;
  }

  return (
    <div className="pb-3 border-b border-border mb-4">
      <h4 className="text-sm font-semibold mb-2 text-muted-foreground">
        Previous Revisions
      </h4>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">Previous invoice revisions</caption>
          <thead>
            <tr className="border-b-2 border-border">
              <th
                scope="col"
                className="py-2 px-2 text-left font-semibold text-muted-foreground"
              >
                Date
              </th>
              <th
                scope="col"
                className="py-2 px-2 text-left font-semibold text-muted-foreground"
              >
                Reference
              </th>
              <th
                scope="col"
                className="py-2 px-2 text-left font-semibold text-muted-foreground"
              >
                Client Ref
              </th>
              <th
                scope="col"
                className="py-2 px-2 text-right font-semibold text-muted-foreground"
              >
                Items
              </th>
              <th
                scope="col"
                className="py-2 px-2 text-right font-semibold text-muted-foreground"
              >
                Net
              </th>
              <th
                scope="col"
                className="py-2 px-2 text-right font-semibold text-muted-foreground"
              >
                Tax
              </th>
              <th
                scope="col"
                className="py-2 px-2 text-right font-semibold text-muted-foreground"
              >
                Gross
              </th>
              <th
                scope="col"
                className="py-2 px-2 text-center font-semibold text-muted-foreground"
              >
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {revisions.map((revision, index) => {
              const items = revision.items ?? [];
              const net = calculateInvoiceNet(items);
              const tax = calculateInvoiceTax(items);
              const gross = calculateInvoiceGross(items);
              const createdDate = revision.created_date
                ? new Date(revision.created_date).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric"
                  })
                : "-";

              return (
                <tr
                  key={revision.id ?? index}
                  className="border-b border-border"
                >
                  <td className="py-2 px-2">{createdDate}</td>
                  <td className="py-2 px-2">{revision.ref ?? "-"}</td>
                  <td className="py-2 px-2">{revision.client_ref ?? "-"}</td>
                  <td className="py-2 px-2 text-right">{items.length}</td>
                  <td className="py-2 px-2 text-right">
                    {currencySymbol}
                    {formatDecimal(net)}
                  </td>
                  <td className="py-2 px-2 text-right">
                    {currencySymbol}
                    {formatDecimal(tax)}
                  </td>
                  <td className="py-2 px-2 text-right">
                    {currencySymbol}
                    {formatDecimal(gross)}
                  </td>
                  <td className="py-2 px-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <EDSButton
                        variant="ghost"
                        size="icon"
                        onClick={() => onViewRevision(revision)}
                        aria-label={`View revision from ${createdDate}`}
                      >
                        <Eye className="w-4 h-4" />
                      </EDSButton>
                      {revision.id && (
                        <EDSButton
                          variant="outline"
                          size="sm"
                          onClick={() => onPrintRevision(revision)}
                          aria-label={`Print revision from ${createdDate}`}
                        >
                          <Printer className="w-3 h-3" />
                          Print
                        </EDSButton>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PreviousRevisions;
